import React from 'react';
import { useTranslation } from 'react-i18next';
import { StringParam, useQueryParam } from 'use-query-params';
import { useRecoilValue } from 'recoil';
import { isMobile } from 'react-device-detect';
import {
    Box,
    Button,
    Card,
    CardActions,
    CardContent,
    CardHeader,
    IconButton, Stack, Table, Tooltip,
    Typography,
} from '@mui/material';
import { Add, Download, Edit, Info, Visibility, VisibilityOff } from '@mui/icons-material';
import Date from 'components/formaters/Date';
import { CarModel, StockStatus } from 'models/StockModel';
import { CompanyType } from 'models/CompanyModel';
import useCarDocModals from 'modules/cars/hooks/useCarDocModals';
import useCars from 'modules/cars/hooks/useCars';
import { ecExpired, originalECRequired } from 'modules/cars/utils/common';
import auth from 'recoil/atoms/auth';


type ExportCertCardProps = {
    car: CarModel,
}

const ExportCertCard = ({car}: ExportCertCardProps) => {
    const my = useRecoilValue(auth);
    const {t} = useTranslation('cars');
    const [docPreview, setDocPreview] = useQueryParam('doc', StringParam);
    const {
        openUploadExpCertModal,
        openAddCertTrackModal,
        openEditExpCertDataModal,
        openCertArrivedModal,
    } = useCarDocModals();
    const {downloadDocument} = useCars();

    const canAction = my.permissions.has('cars update') && (car.status === StockStatus.Pending || my.type === CompanyType.Master);
    const canPreview = [CompanyType.Master, CompanyType.Shipper].includes(my.type);

    const originalRequired = originalECRequired(car);

    const style: any = {mb: 2};
    if (originalRequired) {
        style.border = '1px solid';
        style.borderColor = 'error.main';
    }

    const expired = ecExpired(car);

    return (
        <Card sx={style}>
            <CardHeader
                title={t('export_certificate')}
                action={
                    <>
                        {canAction && (
                            <IconButton
                                aria-label="view certificate"
                                onClick={() => openUploadExpCertModal(car)}
                            >
                                <Add/>
                            </IconButton>
                        )}

                        {car.documents.exportCertificate && (
                            <IconButton
                                aria-label="download certificate"
                                onClick={() => downloadDocument(car.id, 'export_certificate')}
                            >
                                <Download/>
                            </IconButton>
                        )}

                        {car.documents.exportCertificate && !isMobile && canPreview && (
                            <IconButton
                                aria-label="preview document"
                                onClick={() => setDocPreview(docPreview === 'export_certificate' ? undefined : 'export_certificate')}
                            >
                                {docPreview === 'export_certificate' ? <VisibilityOff/> : <Visibility/>}
                            </IconButton>
                        )}
                    </>
                }
            />

            {car.documents.exportCertificate && (
                <CardContent>
                    <Table sx={{
                        'td:first-of-type': {
                            color: 'text.disabled',
                            fontSize: 'smaller',
                        },
                    }}>
                        <tbody>
                        <tr>
                            <td>{t('uploaded_at')}</td>
                            <td><Date date={car.documents.exportCertificate}/></td>
                            {!docPreview && (
                                <td rowSpan={2} style={{textAlign: 'right'}}>
                                    <img
                                        onClick={() => setDocPreview(docPreview === 'export_certificate' ? undefined : 'export_certificate')}
                                        src="/media/ec-preview.png"
                                        alt="ec preview"
                                    />
                                </td>
                            )}
                        </tr>

                        <tr>
                            <td>{t('valid_until')}</td>
                            <td>
                                <Box
                                    component="span"
                                    sx={expired ? {
                                        fontWeight: 'bold',
                                        color: 'error.light',
                                    } : undefined}>
                                    <Date date={car.exportCertificate.validUntil}/>
                                </Box>

                                {canAction && (
                                    <IconButton size="small" onClick={() => openEditExpCertDataModal(car)}>
                                        <Edit fontSize="small"/>
                                    </IconButton>
                                )}
                            </td>
                        </tr>

                        <tr>
                            <td>{t('reference_number')}</td>
                            <td>
                                {car.exportCertificate.referenceNum}
                                {canAction && (
                                    <IconButton size="small" onClick={() => openEditExpCertDataModal(car)}>
                                        <Edit fontSize="small"/>
                                    </IconButton>
                                )}
                            </td>
                        </tr>

                        {car.exportCertificate?.trackCode && (
                            <>
                                <tr>
                                    <td>{t('original_sent_at')}</td>
                                    <td><Date date={car.exportCertificate.originalSentAt}/></td>
                                </tr>
                                <tr>
                                    <td>{t('track_code')}</td>
                                    <td>
                                        <strong>{car.exportCertificate.trackCode}</strong>
                                        <Typography color="text.disabled" fontSize={12}>
                                            by {car.exportCertificate.courier}
                                        </Typography>
                                    </td>
                                </tr>
                            </>
                        )}

                        {car.exportCertificate?.originalArrivedAt && (
                            <tr>
                                <td>{t('original_arrived_at')}</td>
                                <td>
                                    <Stack direction="row" alignItems="center">
                                        <Date date={car.exportCertificate.originalArrivedAt}/>
                                        {my.permissions.has('cars manage') && car.exportCertificate.originalConfirmedBy && (
                                            <Tooltip
                                                title={car.exportCertificate.originalConfirmedBy.name}
                                                placement="top"
                                            >
                                                <Info fontSize="small" color="info"/>
                                            </Tooltip>
                                        )}
                                    </Stack>
                                </td>
                            </tr>
                        )}
                        </tbody>
                    </Table>
                </CardContent>
            )}

            {originalRequired && !car.exportCertificate?.trackCode && (
                <CardContent sx={{color: 'warning.dark'}}>
                    {t('original_ec_required')}
                </CardContent>
            )}

            {originalRequired && (
            <CardActions sx={{justifyContent: 'space-between'}}>
                {my.permissions.has('cars update') && !car.exportCertificate?.trackCode && (
                    <Button
                        onClick={() => openAddCertTrackModal(car)}
                        size="small"
                    >
                        {t('add_tracking_number')}
                    </Button>
                )}

                {my.permissions.has('cars manage') && (
                    <Button
                        onClick={() => openCertArrivedModal(car)}
                        size="small"
                    >
                        {t('original_arrived')}
                    </Button>
                )}
            </CardActions>
            )}
        </Card>
    );
};

export default ExportCertCard;
